import { useState } from "react";
import { Copy } from "lucide-react";
import { CREDENTIAL_SCOPES, CREDENTIAL_SCOPE_LABELS, type CredentialScope } from "@amber/contracts";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldError,
  Overline,
  ReasonDialog,
  ReasonField,
  TextField,
  copyText,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import type { CredentialRow } from "../../contracts/index";
import { accessCommands } from "../queries";
import { AFTER_ACCESS_CHANGE } from "../keys";

function endOfDay(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

export function IssueCredentialDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<CredentialScope[]>(["read_events"]);
  const [expires, setExpires] = useState("");
  const [issued, setIssued] = useState<{ name: string; secret: string } | null>(null);
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      accessCommands.issueCredential(
        { name: name.trim(), scopes, expires_at: expires ? endOfDay(expires) : null, reason: text },
        csrf,
      ),
    invalidate: AFTER_ACCESS_CHANGE,
    onSuccess: (result) => {
      setIssued({ name: result.name, secret: result.secret });
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const today = new Date().toISOString().slice(0, 10);

  if (issued) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title="Credential issued"
        footer={
          <>
            <Button onClick={onClose}>Done</Button>
            <Button
              variant="primary"
              onClick={() => {
                void copyText(issued.secret).then((ok) => {
                  toast.show(ok ? "Secret copied" : "Couldn't copy. Select the secret and copy it.");
                });
              }}
            >
              <Copy aria-hidden="true" />
              Copy secret
            </Button>
          </>
        }
      >
        <DialogText>
          Copy the secret for <b>{issued.name}</b> now. It is shown only this once; Amber keeps only a verifier. The
          consumer sends it as <code className="font-mono">Authorization: Bearer …</code>.
        </DialogText>
        <TextField
          label="Secret"
          value={issued.secret}
          readOnly
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          className="font-mono text-md"
        />
      </Dialog>
    );
  }

  const submit = () => {
    if (reason.submit() && name.trim() && scopes.length > 0) mutation.mutate(reason.trimmed);
  };
  const scopeError =
    fieldErrors["scopes"] ?? (reason.submitted && scopes.length === 0 ? "Choose at least one scope" : null);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Issue a machine credential"
      onSubmit={submit}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm="Issue credential"
          pending={mutation.isPending}
          failed={mutation.isError}
        />
      }
    >
      <DialogText>
        One credential per consumer, so each can be revoked on its own. Every call is recorded in the audit log under
        its name.
      </DialogText>
      <TextField
        label="Name"
        required
        autoComplete="off"
        placeholder="Fact-check desk reader"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        error={fieldErrors["name"] ?? (reason.submitted && !name.trim() ? "Give the credential a name" : null)}
      />
      <fieldset className="mt-3 border-0 p-0" aria-describedby={scopeError ? "credential-scope-err" : undefined}>
        <Overline as="legend" spacing="narrow" className="m-0 mb-1.5 flex items-baseline gap-2 p-0">
          Scope <span className="font-medium normal-case tracking-normal text-attention">required</span>
        </Overline>
        {CREDENTIAL_SCOPES.map((scope) => (
          <label
            key={scope}
            className="flex h-[27px] cursor-pointer items-center gap-2 rounded-md px-1 hover:bg-surface-2"
          >
            <input
              type="checkbox"
              checked={scopes.includes(scope)}
              onChange={() => {
                setScopes(scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope]);
              }}
              className="accent-[var(--color-link)]"
            />
            <span>{CREDENTIAL_SCOPE_LABELS[scope]}</span>
            <span className="text-xs text-muted">{SCOPE_HINTS[scope]}</span>
          </label>
        ))}
        {scopeError ? <FieldError id="credential-scope-err">{scopeError}</FieldError> : null}
      </fieldset>
      <TextField
        label="Expires"
        optional="optional, at the end of that day"
        type="date"
        min={today}
        value={expires}
        onChange={(e) => {
          setExpires(e.target.value);
        }}
        error={fieldErrors["expires_at"] ?? null}
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}

const SCOPE_HINTS: Record<CredentialScope, string> = {
  read_events: "events, changes and archived files",
  read_exports: "snapshots",
  request_checks: "request and read passage checks",
  read_profiles: "profiles, dated roles and the roster",
};

export function RevokeCredentialDialog({ credential, onClose }: { credential: CredentialRow; onClose: () => void }) {
  return (
    <ReasonDialog
      title={`Revoke ${credential.name}?`}
      confirm="Revoke"
      danger
      success={`${credential.name} revoked`}
      onClose={onClose}
      invalidate={AFTER_ACCESS_CHANGE}
      run={(reason, csrf) => accessCommands.revokeCredential(credential.id, reason, csrf)}
    >
      The credential stops working at once, for every call. Its calls stay in the audit log. A consumer that still needs
      access gets a new credential.
    </ReasonDialog>
  );
}
